import { useQueries } from '@tanstack/react-query';
import { useAuth } from '@workos-inc/authkit-react';
import { Duration, sub } from 'date-fns';

import type { API } from 'src/api';
import { getApiQueryKey, refetchInterval, useApi } from 'src/api';
import { identity } from 'src/utils/generic';
import { toObject } from 'src/utils/object';

import { MetricsStep, MetricsTimeFrame, getDefaultStep } from './metrics-helpers';
import { DataPoint, Metric } from './metrics-types';

const timeFrameToDuration: Record<MetricsTimeFrame, Duration> = {
  '5m': { minutes: 5 },
  '15m': { minutes: 15 },
  '1h': { hours: 1 },
  '6h': { hours: 6 },
  '1d': { days: 1 },
  '2d': { days: 2 },
  '7d': { days: 7 },
};

type UseMetricsOptions = {
  serviceId?: string;
  instanceId?: string;
  metrics: API.MetricName[];
  timeFrame: MetricsTimeFrame;
  step?: MetricsStep;
};

export function useMetricsQueries({ serviceId, instanceId, metrics, timeFrame, step }: UseMetricsOptions) {
  const api = useApi();
  const { getAccessToken } = useAuth();

  const resolvedStep = step ?? getDefaultStep(timeFrame);

  return useQueries({
    queries: metrics.map((name) => {
      const query = {
        name,
        service_id: serviceId,
        instance_id: instanceId,
        step: resolvedStep,
        time_frame: timeFrame,
      };

      return {
        meta: { getAccessToken, showError: false },
        refetchInterval: refetchInterval(60 * 1000, 5 * 60 * 1000),
        queryKey: getApiQueryKey('get /v1/streams/metrics', { query }, api),
        queryFn: () => {
          const duration = timeFrameToDuration[timeFrame];
          const start = sub(new Date(), duration).toISOString();

          return api('get /v1/streams/metrics', {
            query: { ...query, start },
          });
        },
        select(data: API.GetMetricsReply) {
          return data.metrics!.map(({ labels, samples }) => ({
            labels,
            samples: samples!.map(
              ({ timestamp, value }): DataPoint => ({
                date: timestamp!,
                value: value ?? undefined,
              }),
            ),
          }));
        },
      };
    }),
    combine: (queries) => ({
      isPending: queries.some((query) => query.isPending),
      isError: queries.some((query) => query.isError),
      error: toObject(metrics, identity, (_, index) => queries[index]?.error),
      data: toObject(metrics, identity, (_, index): Metric[] | undefined => queries[index]?.data),
    }),
  });
}
